import { type ClassValue, clsx } from 'clsx'

// Utility for merging Tailwind classes
export function cn(...inputs: ClassValue[]): string {
  return clsx(inputs)
}

// Format date utilities
export const dateUtils = {
  // Format date to readable string
  formatDate: (date: string | Date): string => {
    // Extract date part from ISO string without timezone conversion
    const dateStr = typeof date === 'string' ? date : date.toISOString()
    const datePart = dateStr.split('T')[0]
    const [year, month, day] = datePart.split('-')

    const monthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ]

    const monthIndex = parseInt(month, 10) - 1
    const dayNum = parseInt(day, 10)

    return `${monthNames[monthIndex]} ${dayNum}, ${year}`
  },

  // Format date with time
  formatDateTime: (date: string | Date): string => {
    const d = new Date(date)
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  },

  // Get relative time (e.g., "2 hours ago")
  getRelativeTime: (date: string | Date): string => {
    const d = new Date(date)
    const now = new Date()
    const diff = now.getTime() - d.getTime()

    const minute = 60 * 1000
    const hour = minute * 60
    const day = hour * 24
    const week = day * 7
    const month = day * 30
    const year = day * 365

    if (diff < minute) return 'just now'
    if (diff < hour) return `${Math.floor(diff / minute)}m ago`
    if (diff < day) return `${Math.floor(diff / hour)}h ago`
    if (diff < week) return `${Math.floor(diff / day)}d ago`
    if (diff < month) return `${Math.floor(diff / week)}w ago`
    if (diff < year) return `${Math.floor(diff / month)}mo ago`
    return `${Math.floor(diff / year)}y ago`
  },

  // Format date as "DD Month" (e.g., "10 September")
  formatDateDisplay: (date: string | Date): string => {
    // Extract date part from ISO string without timezone conversion
    const dateStr = typeof date === 'string' ? date : date.toISOString()
    const datePart = dateStr.split('T')[0]
    const [, month, day] = datePart.split('-')

    const monthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ]

    const monthIndex = parseInt(month, 10) - 1
    const dayNum = parseInt(day, 10)

    return `${dayNum} ${monthNames[monthIndex]}`
  },

  // Format date as "DD Mon, YYYY" (e.g., "11 Jan, 2025")
  formatBookingStatusDate: (date: string | Date): string => {
    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ]

    let year: string
    let month: string
    let day: string

    if (typeof date === 'string') {
      // Extract date part from ISO string without timezone conversion
      const datePart = date.split('T')[0]
      ;[year, month, day] = datePart.split('-')
    } else {
      // For Date objects, extract components directly without timezone conversion
      year = date.getFullYear().toString()
      month = (date.getMonth() + 1).toString().padStart(2, '0')
      day = date.getDate().toString().padStart(2, '0')
    }

    const monthIndex = parseInt(month, 10) - 1
    const dayNum = parseInt(day, 10)

    return `${dayNum} ${monthNames[monthIndex]}, ${year}`
  },

  // Parse ISO 8601 string to Date object
  parseDDMMYYYY: (dateStr: string): Date | null => {
    if (!dateStr || typeof dateStr !== 'string') return null

    // Parse ISO 8601 format
    const date = new Date(dateStr)

    // Verify the date is valid
    if (isNaN(date.getTime())) {
      return null
    }

    return date
  },

  // Format Date object to dd.mm.yyyy string
  formatToDDMMYYYY: (date: Date): string => {
    if (!(date instanceof Date) || isNaN(date.getTime())) return ''

    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()

    return `${day}.${month}.${year}`
  },

  // Check if date is in the past (before today)
  isInPast: (date: Date): boolean => {
    if (!(date instanceof Date) || isNaN(date.getTime())) return false

    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const checkDate = new Date(date)
    checkDate.setHours(0, 0, 0, 0)

    return checkDate < today
  },

  // Subtract days from a date and return ISO string (preserves local timezone)
  subtractDays: (dateStr: string, days: number): string => {
    const date = new Date(dateStr)
    date.setDate(date.getDate() - days)

    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')

    return `${year}-${month}-${day}T00:00:00.000Z`
  },

  // Get today's date as ISO string (preserves local timezone)
  getTodayISO: (): string => {
    const today = new Date()
    const year = today.getFullYear()
    const month = String(today.getMonth() + 1).padStart(2, '0')
    const day = String(today.getDate()).padStart(2, '0')

    return `${year}-${month}-${day}T00:00:00.000Z`
  },

  // Convert Date to ISO string preserving local date (no timezone conversion)
  toLocalISOString: (date: Date): string => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')

    return `${year}-${month}-${day}T00:00:00.000Z`
  },

  // Validate ISO 8601 date format
  validateDDMMYYYY: (dateStr: string): { valid: boolean; error?: string } => {
    if (!dateStr || typeof dateStr !== 'string') {
      return { valid: false, error: 'Date is required' }
    }

    // Parse ISO 8601 format
    const date = new Date(dateStr)

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return { valid: false, error: 'Invalid date format' }
    }

    return { valid: true }
  },

  // Validate time format HH:MM
  validateTime: (timeStr: string): { valid: boolean; error?: string } => {
    if (!timeStr || typeof timeStr !== 'string') {
      return { valid: false, error: 'Time is required' }
    }

    // Check format with regex
    const timeRegex = /^(\d{2}):(\d{2})$/
    const match = timeStr.match(timeRegex)

    if (!match) {
      return { valid: false, error: 'Format must be HH:MM' }
    }

    const hours = parseInt(match[1], 10)
    const minutes = parseInt(match[2], 10)

    // Validate hours (0-23)
    if (hours < 0 || hours > 23) {
      return { valid: false, error: 'Hours must be between 00 and 23' }
    }

    // Validate minutes (0-59)
    if (minutes < 0 || minutes > 59) {
      return { valid: false, error: 'Minutes must be between 00 and 59' }
    }

    return { valid: true }
  },
}

// String utilities
export const stringUtils = {
  // Capitalize first letter
  capitalize: (str: string): string => {
    return str.charAt(0).toUpperCase() + str.slice(1)
  },

  // Convert to title case
  toTitleCase: (str: string): string => {
    return str.replace(
      /\w\S*/g,
      txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
    )
  },

  // Generate slug from string
  generateSlug: (str: string): string => {
    return str
      .toLowerCase()
      .replace(/[^\w ]+/g, '')
      .replace(/ +/g, '-')
  },

  // Truncate string with ellipsis
  truncate: (str: string, length: number): string => {
    return str.length > length ? `${str.substring(0, length)}...` : str
  },
}

// Validation utilities
export const validationUtils = {
  // Email validation
  isValidEmail: (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  },

  // Password strength validation
  isStrongPassword: (password: string): boolean => {
    // At least 6 characters and no whitespace
    return password.length >= 6 && !/\s/.test(password)
  },

  // URL validation
  isValidUrl: (url: string): boolean => {
    try {
      new URL(url)
      return true
    } catch {
      return false
    }
  },
}

// Local storage utilities with error handling
export const storageUtils = {
  // Set item in localStorage
  setItem: (key: string, value: any): void => {
    try {
      if (typeof window !== 'undefined') {
        localStorage.setItem(key, JSON.stringify(value))
      }
    } catch (error) {
      console.error('Error saving to localStorage:', error)
    }
  },

  // Get item from localStorage
  getItem: <T>(key: string, defaultValue?: T): T | null => {
    try {
      if (typeof window !== 'undefined') {
        const item = localStorage.getItem(key)
        return item ? JSON.parse(item) : defaultValue || null
      }
      return defaultValue || null
    } catch (error) {
      console.error('Error reading from localStorage:', error)
      return defaultValue || null
    }
  },

  // Remove item from localStorage
  removeItem: (key: string): void => {
    try {
      if (typeof window !== 'undefined') {
        localStorage.removeItem(key)
      }
    } catch (error) {
      console.error('Error removing from localStorage:', error)
    }
  },
}

// Debounce utility for search/input handling
export const debounce = <T extends (..._args: any[]) => any>(
  func: T,
  wait: number
): ((..._args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout
  return (..._args: Parameters<T>) => {
    clearTimeout(timeout)
    timeout = setTimeout(() => func(..._args), wait)
  }
}

// API error handling utility
export const apiErrorUtils = {
  // Extract error message from API response
  getErrorMessage: (
    response: any,
    fallback: string = 'An error occurred'
  ): string => {
    // Handle API error response format: {"message":["Error message"]}
    console.log('ERROR', response)
    if (
      response?.message &&
      Array.isArray(response.message) &&
      response.message.length > 0
    ) {
      return response.message[0]
    }
    // Handle direct string error
    if (typeof response?.error === 'string') {
      return response.error
    }
    // Handle direct message string
    if (typeof response?.message === 'string') {
      return response.message
    }
    // Return fallback message
    return fallback
  },
}

//Capitalize Full Name
export const capitalizeFullName = (name: any) => {
  return name
    ?.toLowerCase()
    ?.replace(/\b\w/g, (char: string) => char.toUpperCase())
}

// Format booking status - converts camelCase/PascalCase to properly spaced text
export const formatBookingStatus = (status: string): string => {
  if (!status) return ''

  // If status already has spaces, return as is
  if (status.includes(' ')) return status

  // Convert camelCase/PascalCase to spaced text
  // Add space before uppercase letters
  const formatted = status.replace(/([A-Z])/g, ' $1').trim()

  // Capitalize first letter and lowercase 'to' if present
  return formatted
    .split(' ')
    .map((word, index) => {
      if (word.toLowerCase() === 'to') {
        return 'to'
      }
      return index === 0 ? word : word
    })
    .join(' ')
}
